/**
 * OrderRow - Fila expandible de orden en resultados de búsqueda
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronRight, ChevronDown, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { BusquedaOrden } from "@/hooks/use-busqueda-avanzada";

interface OrderRowProps {
  orden: BusquedaOrden;
  onCloseSheet: () => void;
}

function getEstadoBadgeVariant(
  estado: string
): "default" | "secondary" | "destructive" | "outline" {
  switch (estado) {
    case "FINALIZADA":
      return "default";
    case "BAJA":
      return "destructive";
    case "EN_EXTRACCION":
      return "outline";
    default:
      return "secondary";
  }
}

export function OrderRow({ orden, onCloseSheet }: OrderRowProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  const handleViewOrden = () => {
    onCloseSheet();
    navigate("/ordenes", {
      state: { openOrdenId: orden.id },
    });
  };

  return (
    <div className="rounded-md border bg-muted/20">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left px-3 py-2 hover:bg-muted/50 transition-colors rounded-md"
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-2">
          <div className="text-muted-foreground">
            {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          </div>

          <span className="font-mono text-xs font-medium">{orden.numero}</span>

          <Badge variant={getEstadoBadgeVariant(orden.estado)} className="text-[10px] h-5">
            {orden.estado}
          </Badge>

          <span className="text-xs text-muted-foreground ml-auto hidden sm:inline">
            {orden.sucursal_nombre}
          </span>

          <span className="text-xs text-muted-foreground">
            {new Date(orden.fecha).toLocaleDateString()}
          </span>
        </div>
      </button>

      {expanded && (
        <div className="px-3 pb-2 pt-1 border-t">
          {/* View order action */}
          <div className="mb-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-xs px-2"
              onClick={handleViewOrden}
            >
              <ExternalLink className="mr-1 h-3 w-3" />
              {t("busquedaAvanzada.actions.verOrden")}
            </Button>
          </div>

          {/* Exams list */}
          {orden.examenes.length > 0 ? (
            <div className="space-y-1">
              {orden.examenes.map((examen) => (
                <div
                  key={examen.id}
                  className="flex items-center justify-between text-xs py-1 px-2 rounded bg-background"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-mono text-muted-foreground shrink-0">
                      {examen.examen_codigo}
                    </span>
                    <span className="truncate">{examen.examen_nombre}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-2">
                    <span className="text-muted-foreground">{examen.area}</span>
                    <Badge variant="outline" className="text-[10px] h-4">
                      {examen.estado}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground py-1">
              {t("busquedaAvanzada.results.noExamenes")}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
